import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, FileText, Info, RotateCcw } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { Reveal } from "@/lib/motion";
import { trackEvent } from "@/lib/api";
import { DISCLAIMER_ORIENTADOR, CTAS, whatsappHref, SITE } from "@/content/site";
import {
  OPCIONES_MOMENTO,
  OPCIONES_URGENCIA,
  TEMAS_GENERALES,
  TEMAS_POR_MOMENTO,
  calcularResultado,
} from "@/content/orientador";
import { servicioBySlug } from "@/content/services";

const SIN_CLARO = "__sin_claro__";

export default function Orientador() {
  const [paso, setPaso] = useState(0);
  const [momentoSlug, setMomentoSlug] = useState<string | null>(null);
  const [temas, setTemas] = useState<string[]>([]);
  const [urgenciaId, setUrgenciaId] = useState<string | null>(null);

  const momentoReal = momentoSlug === SIN_CLARO ? null : momentoSlug;

  const temasDisponibles = useMemo(
    () => (momentoReal ? TEMAS_POR_MOMENTO[momentoReal] || [] : TEMAS_GENERALES),
    [momentoReal]
  );

  const resultado = useMemo(
    () =>
      paso === 3
        ? calcularResultado(
            momentoReal,
            temasDisponibles.filter((t) => temas.includes(t.id)),
            urgenciaId
          )
        : null,
    [paso, momentoReal, temasDisponibles, temas, urgenciaId]
  );

  useEffect(() => {
    if (resultado) {
      trackEvent("orientador_result", { area: resultado.areaPrincipalId, urgente: resultado.urgente });
    }
  }, [resultado]);

  const puedeAvanzar =
    (paso === 0 && momentoSlug !== null) ||
    (paso === 1 && temas.length >= 1) ||
    (paso === 2 && urgenciaId !== null);

  const reiniciar = () => {
    setPaso(0);
    setMomentoSlug(null);
    setTemas([]);
    setUrgenciaId(null);
  };

  const area = resultado ? servicioBySlug(resultado.areaPrincipalId) : null;
  const contactoHref = `/contacto?origen=orientador&urgencia=${resultado?.urgente ? "prioritaria" : "normal"}${
    area ? `&tema=${encodeURIComponent(area.nombre)}` : ""
  }`;

  return (
    <>
      <Seo
        title="Encuentra tu ruta"
        description="Orientador de Tulevik Legal: responde tres preguntas y obtén el área sugerida, los documentos a preparar y el siguiente paso recomendado. Sin registro y sin diagnóstico legal."
        path="/orientador"
      />
      <PageHero
        eyebrow="Orientador"
        titulo="Encuentra tu ruta"
        lead="Tres preguntas para orientarte: qué área aplica, qué documentos preparar y cuál es el siguiente paso. Sin registro, sin diagnóstico legal y sin guardar tus respuestas."
      />

      <section className="bg-parchment">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-8 sm:py-24">
          {paso < 3 && (
            <div className="mb-10" data-testid="orientador-progreso">
              <div className="flex items-center justify-between">
                <p className="font-mono text-xs tracking-[0.22em] text-azul">
                  PASO {paso + 1} DE 3
                </p>
                {paso > 0 && (
                  <button
                    onClick={() => setPaso(paso - 1)}
                    data-testid="orientador-atras-button"
                    className="inline-flex items-center gap-1.5 text-sm text-ink-muted transition-colors hover:text-ink-deep"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Atrás
                  </button>
                )}
              </div>
              <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-hairline">
                <motion.div
                  className="h-full bg-azul"
                  initial={{ width: "0%" }}
                  animate={{ width: `${((paso + 1) / 3) * 100}%` }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {paso === 0 && (
              <motion.div key="paso0" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">¿Qué momento estás viviendo?</h2>
                <p className="mt-2 text-sm text-ink-muted">Elige el que más se acerque. No tiene que ser perfecto.</p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {OPCIONES_MOMENTO.map((op) => (
                    <button
                      key={op.slug || "sin-claro"}
                      type="button"
                      data-testid={`orientador-momento-${op.slug || "no-claro"}`}
                      onClick={() => {
                        setMomentoSlug(op.slug || SIN_CLARO);
                        setTemas([]);
                      }}
                      className={`border p-4 text-left text-sm transition-all duration-200 ${
                        momentoSlug === (op.slug || SIN_CLARO)
                          ? "border-azul bg-azul-light/60 text-ink-deep"
                          : "border-hairline bg-white text-ink-text hover:border-azul/50"
                      }`}
                    >
                      {op.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {paso === 1 && (
              <motion.div key="paso1" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">¿Qué tema se parece más a tu asunto?</h2>
                <p className="mt-2 text-sm text-ink-muted">
                  Elige hasta tres. Si hay varios asuntos al mismo tiempo, no te preocupes: los consideramos.
                </p>
                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {temasDisponibles.map((t) => {
                    const activo = temas.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`orientador-tema-${t.id}`}
                        onClick={() =>
                          setTemas((prev) =>
                            prev.includes(t.id)
                              ? prev.filter((x) => x !== t.id)
                              : prev.length < 3
                              ? [...prev, t.id]
                              : prev
                          )
                        }
                        className={`flex items-start justify-between gap-3 border p-4 text-left text-sm transition-all duration-200 ${
                          activo
                            ? "border-azul bg-azul-light/60 text-ink-deep"
                            : "border-hairline bg-white text-ink-text hover:border-azul/50"
                        }`}
                      >
                        <span>{t.label}</span>
                        {activo && <Check className="mt-0.5 h-4 w-4 shrink-0 text-azul" strokeWidth={2.4} />}
                      </button>
                    );
                  })}
                </div>
                {temas.length > 0 && (
                  <p className="mt-4 font-mono text-xs text-ink-muted">{temas.length} de 3 seleccionados</p>
                )}
              </motion.div>
            )}

            {paso === 2 && (
              <motion.div key="paso2" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }}>
                <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">¿Hay fecha o urgencia encima de la mesa?</h2>
                <p className="mt-2 text-sm text-ink-muted">Sé honesto: esto solo define qué tan rápido hay que moverse.</p>
                <div className="mt-8 grid gap-3">
                  {OPCIONES_URGENCIA.map((op) => (
                    <button
                      key={op.id}
                      type="button"
                      data-testid={`orientador-urgencia-${op.id}`}
                      onClick={() => setUrgenciaId(op.id)}
                      className={`border p-5 text-left text-sm transition-all duration-200 ${
                        urgenciaId === op.id
                          ? "border-azul bg-azul-light/60 text-ink-deep"
                          : "border-hairline bg-white text-ink-text hover:border-azul/50"
                      }`}
                    >
                      {op.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {paso < 3 && (
            <div className="mt-10 flex justify-end">
              <button
                type="button"
                onClick={() => puedeAvanzar && setPaso(paso + 1)}
                disabled={!puedeAvanzar}
                data-testid="orientador-continuar-button"
                className="btn-primary disabled:cursor-not-allowed disabled:opacity-40"
              >
                {paso === 2 ? "Ver mi ruta" : "Continuar"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {paso === 3 && resultado && area && (
            <motion.div
              key="resultado"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="orientador-resultado"
            >
              <p className="eyebrow mb-4">Tu ruta sugerida</p>
              <h2 className="font-serif text-3xl leading-tight text-ink-deep sm:text-4xl">{area.nombre}</h2>
              <p className="mt-3 text-ink-muted">{area.frase}</p>

              <div className="mt-8 border border-hairline bg-white p-6 sm:p-8" data-testid="orientador-area-principal">
                <p className="eyebrow mb-3">Área principal sugerida</p>
                <Link
                  to={`/servicios/${area.slug}`}
                  data-testid="orientador-area-link"
                  className="inline-flex items-center gap-2 font-medium text-azul underline-offset-4 hover:underline"
                >
                  Conocer {area.nombre}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {resultado.relacionados.length > 0 && (
                <div className="mt-6" data-testid="orientador-relacionados">
                  <p className="eyebrow mb-4">Temas relacionados</p>
                  <div className="flex flex-wrap gap-2">
                    {resultado.relacionados.map((slug) => {
                      const s = servicioBySlug(slug);
                      if (!s) return null;
                      return (
                        <Link
                          key={slug}
                          to={`/servicios/${slug}`}
                          data-testid={`orientador-relacionado-${slug}`}
                          className="border border-hairline bg-white px-4 py-2 text-sm text-ink-text transition-colors hover:border-azul/50 hover:text-azul"
                        >
                          {s.nombre}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-6 border border-hairline bg-parchment-surface/60 p-6 sm:p-8" data-testid="orientador-documentos">
                <p className="eyebrow mb-5 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-azul" />
                  Documentos o información a preparar
                </p>
                <ul className="space-y-2.5">
                  {resultado.docs.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-ink-text">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-salvia" strokeWidth={2.2} />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 border-l-2 border-azul bg-azul-light/40 p-6" data-testid="orientador-siguiente-paso">
                <p className="eyebrow mb-3">Siguiente paso recomendado</p>
                <p className="text-sm leading-relaxed text-ink-text">{resultado.textoSiguiente}</p>
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  {resultado.urgente && (
                    <Link to="/atencion-prioritaria" data-testid="orientador-cta-prioritaria" className="btn-primary">
                      {CTAS.prioritaria.label}
                    </Link>
                  )}
                  <Link to={contactoHref} data-testid="orientador-cta-contacto" className={resultado.urgente ? "btn-secondary" : "btn-primary"}>
                    {CTAS.principal.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  {!resultado.urgente && SITE.WHATSAPP_NUMERO && (
                    <a href={whatsappHref()} target="_blank" rel="noopener noreferrer" data-testid="orientador-cta-whatsapp" className="btn-secondary">
                      {CTAS.secundaria.label}
                    </a>
                  )}
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={reiniciar}
                  data-testid="orientador-reiniciar-button"
                  className="inline-flex items-center gap-2 text-sm text-ink-muted underline-offset-4 hover:text-ink-deep hover:underline"
                >
                  <RotateCcw className="h-4 w-4" />
                  Empezar de nuevo
                </button>
                <p className="flex max-w-md items-start gap-2 text-xs text-ink-muted">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {DISCLAIMER_ORIENTADOR}
                </p>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
